import React, { useState, useEffect } from 'react';

/**
 * Inline Score Input component (− [input] +)
 * - Buttons fire onChange immediately (step by 0.5)
 * - Input field accepts 0.0–10.0 with one decimal
 * - Syncs on Enter or blur
 * - Internally maps 0-10 display scale to 0-100 AniList API scale
 */
const ScoreInput = ({ mediaId, currentScore, isUpdating, onChange }) => {
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState('');

    // Convert AniList 0-100 raw score to display 0-10 scale
    const displayScore = currentScore > 0 ? (currentScore / 10) : 0;

    // Sync local value when external score changes
    useEffect(() => {
        if (!editing) {
            setLocalValue(displayScore > 0 ? displayScore.toFixed(1) : '—');
        }
    }, [currentScore, editing, displayScore]);

    const handleDecrement = (e) => {
        e.stopPropagation();
        const newDisplay = Math.max(0, displayScore - 0.5);
        const newRaw = Math.round(newDisplay * 10);
        onChange(newRaw);
    };

    const handleIncrement = (e) => {
        e.stopPropagation();
        const newDisplay = Math.min(10, displayScore + 0.5);
        const newRaw = Math.round(newDisplay * 10);
        onChange(newRaw);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        // Allow empty, digits, and one decimal point
        if (val === '' || val === '—' || /^\d{0,2}\.?\d{0,1}$/.test(val)) {
            setLocalValue(val);
        }
    };

    const commitValue = () => {
        setEditing(false);
        if (localValue === '' || localValue === '—') {
            // Set to 0 (no score)
            if (currentScore !== 0) onChange(0);
            return;
        }
        const numVal = parseFloat(localValue);
        if (isNaN(numVal)) return;
        // Clamp to 0-10
        const clamped = Math.min(10, Math.max(0, numVal));
        // Round to nearest 0.5 and convert to 0-100 AniList scale
        const rounded = Math.round(clamped * 2) / 2;
        const rawScore = Math.round(rounded * 10);
        if (rawScore !== currentScore) {
            onChange(rawScore);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
        e.stopPropagation();
    };

    const handleFocus = (e) => {
        setEditing(true);
        setLocalValue(displayScore > 0 ? displayScore.toFixed(1) : '');
        e.target.select();
    };

    return (
        <div className="score-input" onClick={(e) => e.stopPropagation()}>
            <span className="score-label">⭐</span>
            <button
                className="ep-btn ep-dec"
                onClick={handleDecrement}
                disabled={isUpdating || displayScore <= 0}
                title="Decrease score"
            >
                −
            </button>
            <input
                type="text"
                className={`score-field ${isUpdating ? 'updating' : ''}`}
                value={editing ? localValue : (displayScore > 0 ? displayScore.toFixed(1) : '—')}
                onFocus={handleFocus}
                onBlur={commitValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isUpdating}
                title={displayScore > 0 ? `Score: ${displayScore.toFixed(1)} / 10` : 'No score'}
                placeholder="—"
            />
            <button
                className="ep-btn ep-inc"
                onClick={handleIncrement}
                disabled={isUpdating || displayScore >= 10}
                title="Increase score"
            >
                +
            </button>
        </div>
    );
};

export default ScoreInput;
